/**
 * Early Session Restoration
 * 
 * CRITICAL: This runs BEFORE any UI renders to restore the session
 * and prevent "signed out" flicker during app startup.
 * 
 * Strategy:
 * 1. Check persisted auth state from localStorage (Zustand persist)
 * 2. If auth state exists, preserve it while restoring Supabase session
 * 3. Attempt token refresh if needed (with retry logic)
 * 4. Only clear auth state if token refresh definitively fails
 * 5. Return early success if session is valid
 */

import { getSession, getCurrentUser } from "@/lib/auth/auth";
import { supabase, refreshSessionWithLock, isAuthBackoffActive } from "@/lib/supabase/client";
import {
  isDefinitiveInvalidRefreshTokenError,
  isTransientRefreshError,
} from "@/lib/auth/refresh-token-errors";
import { useAuthStore } from "@/stores/auth-store";

export interface SessionRestoreResult {
  success: boolean;
  hasSession: boolean;
  hasUser: boolean;
  userId?: string;
  error?: string;
  requiresLogin?: boolean;
}

const MAX_RETRIES = 3;
const RETRY_DELAYS = [500, 1000, 2000]; // Exponential backoff

/**
 * Restore session with retry logic
 * This ensures we don't log out users on temporary network issues
 */
export async function restoreSessionWithRetry(): Promise<SessionRestoreResult> {
  // Get persisted auth state first (needed for backoff + intentional-logout handling)
  const persistedState = useAuthStore.getState();
  const hadPersistedAuth = !!(persistedState.user && persistedState.isAuthenticated);

  // CRITICAL: Skip restoration when auth backoff is active (429/400 loop break).
  // Never force login for returning users — backoff is a temporary guard, not proof of logout.
  if (isAuthBackoffActive()) {
    return {
      success: false,
      hasSession: false,
      hasUser: false,
      requiresLogin: hadPersistedAuth ? false : true,
      error: hadPersistedAuth ? "Auth temporarily rate-limited; session preserved." : undefined,
    };
  }

  // CRITICAL: Skip when user intentionally logged out
  if (typeof window !== "undefined") {
    try {
      const { isIntentionalLogout } = await import("@/lib/auth/logout-flag");
      if (isIntentionalLogout()) {
        return {
          success: false,
          hasSession: false,
          hasUser: false,
          requiresLogin: true,
        };
      }
    } catch {
      // Ignore
    }
  }
  
  // If we have persisted auth state, preserve it while checking session
  
  // Try to restore session with retries
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      // First, check if we already have a valid session
      let existingSession = await getSession();
      
      // If session exists but access token is expired or close to expiring, refresh it proactively
      if (existingSession?.access_token && existingSession?.user && existingSession?.expires_at) {
        const expiresAt = existingSession.expires_at * 1000; // Convert to milliseconds
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;
        const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
        
        // If token expires in less than 5 minutes, refresh it proactively
        if (timeUntilExpiry < fiveMinutes && existingSession.refresh_token) {
          try {
            const { data: { session: refreshedSession }, error: refreshError } = await refreshSessionWithLock();
            if (refreshedSession && !refreshError) {
              existingSession = refreshedSession;
            }
          } catch {
            // Use existing session on proactive refresh error
          }
        }
      }
      
      if (existingSession?.access_token && existingSession?.user) {
        
        // Sync the session to auth store
        await useAuthStore.getState().syncSession();
        
        return {
          success: true,
          hasSession: true,
          hasUser: true,
          userId: existingSession.user.id,
        };
      }
      
      // CRITICAL: Only attempt refresh when we have a refresh token in storage.
      // Calling refreshSession with no token causes unnecessary auth API requests
      // and can trigger 429 rate limiting (especially in production/shared IPs).
      if (!existingSession?.refresh_token) {
        if (hadPersistedAuth && attempt < MAX_RETRIES - 1) {
          const delay = RETRY_DELAYS[attempt];
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        if (hadPersistedAuth) {
          return {
            success: false,
            hasSession: false,
            hasUser: false,
            error: "Unable to verify session yet. Please try again.",
            requiresLogin: false,
          };
        }
        return {
          success: false,
          hasSession: false,
          hasUser: false,
          requiresLogin: true,
        };
      }
      
      const { data: { session: refreshedSession }, error: refreshError } = await refreshSessionWithLock();
      
      if (refreshedSession?.access_token && refreshedSession?.user) {
        
        // Sync the session to auth store
        await useAuthStore.getState().syncSession();
        
        return {
          success: true,
          hasSession: true,
          hasUser: true,
          userId: refreshedSession.user.id,
        };
      }
      
      // CRITICAL: Social apps (Instagram, Facebook, Hinge) are very forgiving with refresh failures
      // Only treat as invalid if we're absolutely certain the token is expired/invalid
      if (refreshError) {
        const isInvalidToken = isDefinitiveInvalidRefreshTokenError(refreshError);
        const isTransientError = isTransientRefreshError(refreshError);

        if (isInvalidToken) {
          if (hadPersistedAuth) {
            await useAuthStore.getState().logout();
          }
          
          return {
            success: false,
            hasSession: false,
            hasUser: false,
            error: "Session expired. Please log in again.",
            requiresLogin: true,
          };
        } else if (isTransientError) {
          // Try to get existing session from storage
          const existingSession = await getSession();
          if (existingSession?.access_token) {
            return {
              success: true,
              hasSession: true,
              hasUser: true,
              userId: existingSession.user?.id,
            };
          }
          // If no session, preserve persisted auth if it exists
          if (hadPersistedAuth) {
            return {
              success: false,
              hasSession: false,
              hasUser: false,
              error: "Temporary refresh failure. Session preserved.",
              requiresLogin: false, // Don't force login on network errors
            };
          }
        }
      }
      
      // If refresh didn't work, try getting existing session
      const session = await getSession();
      const user = await getCurrentUser();
      
      if (session?.access_token && user) {
        
        // Sync the session to auth store
        await useAuthStore.getState().syncSession();
        
        return {
          success: true,
          hasSession: true,
          hasUser: true,
          userId: user.id,
        };
      }
      
      if (hadPersistedAuth && attempt < MAX_RETRIES - 1) {
        const delay = RETRY_DELAYS[attempt];
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      if (hadPersistedAuth) {
        return {
          success: false,
          hasSession: false,
          hasUser: false,
          error: "Unable to verify session yet. Please try again.",
          requiresLogin: false,
        };
      }
      
      // No persisted auth and no session - user is logged out
      return {
        success: false,
        hasSession: false,
        hasUser: false,
        requiresLogin: true,
      };
      
    } catch (error: any) {
      const isInvalidToken = isDefinitiveInvalidRefreshTokenError(error);
      
      if (isInvalidToken) {
        if (hadPersistedAuth) {
          await useAuthStore.getState().logout();
        }
        
        return {
          success: false,
          hasSession: false,
          hasUser: false,
          error: "Session expired. Please log in again.",
          requiresLogin: true,
        };
      }
      
      if (attempt < MAX_RETRIES - 1) {
        const delay = RETRY_DELAYS[attempt];
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      if (hadPersistedAuth) {
        return {
          success: false,
          hasSession: false,
          hasUser: false,
          error: "Unable to verify session. Please try again.",
          requiresLogin: false, // Don't force login - might be temporary
        };
      }
      
      return {
        success: false,
        hasSession: false,
        hasUser: false,
        error: error?.message || "Session restoration failed",
        requiresLogin: true,
      };
    }
  }
  
  // Should never reach here, but TypeScript needs it
  return {
    success: false,
    hasSession: false,
    hasUser: false,
    requiresLogin: true,
  };
}

/**
 * Quick session check - used for early validation
 * Doesn't do full restoration, just checks if session exists
 */
export async function quickSessionCheck(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session?.access_token;
  } catch {
    return false;
  }
}

